import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, ShoppingBag, Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../api/axios';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';
import useNotepadStore from '../store/notepadStore';
import { toast } from 'sonner';
import { formatEUR } from '../utils/format';

const Notepad = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const addItem = useCartStore(state => state.addItem);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const removeFromNotepad = useNotepadStore(state => state.removeFromNotepad);
  const addToNotepad = useNotepadStore(state => state.addToNotepad);
  const fetchNotepad = useNotepadStore(state => state.fetchNotepad);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchNotepadData();
  }, [isAuthenticated]);

  const fetchNotepadData = async () => {
    setLoading(true);
    try {
      const notepadResponse = await axiosInstance.get('/notepad');
      
      if (notepadResponse.data.products) {
        setProducts(notepadResponse.data.products);
      }
      
      await fetchNotepad();
    } catch (error) {
      console.error('Error fetching notepad:', error);
      toast.error('Fehler beim Laden des Merkzettels');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    const success = await removeFromNotepad(productId);
    if (success) {
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Vom Merkzettel entfernt');
    } else {
      toast.error('Fehler beim Entfernen');
    }
  };

  const handleQuantityChange = async (productId, currentQuantity, change) => {
    const newQuantity = Math.max(1, Math.min(999, currentQuantity + change));
    
    if (newQuantity !== currentQuantity) {
      const success = await addToNotepad(productId, newQuantity);
      
      if (success) {
        setProducts(products.map(p => 
          p.id === productId ? { ...p, notepad_quantity: newQuantity } : p
        ));
      } else {
        toast.error('Fehler beim Aktualisieren der Menge');
      }
    }
  };

  const handleAddToCart = (product) => {
    const quantity = product.notepad_quantity || 1;
    if (!addItem(product, quantity)) return;
    toast.success(`${quantity}x ${product.name} zum Warenkorb hinzugefügt`);
  };

  const handleAddAllToCart = () => {
    let totalItems = 0;
    products.forEach(product => {
      const quantity = product.notepad_quantity || 1;
      if (addItem(product, quantity)) totalItems += quantity;
    });
    toast.success(`${totalItems} Artikel zum Warenkorb hinzugefügt`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#E8E0D5' }}>
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="notepad-title">
            📋 Mein Merkzettel
          </h1>
          {products.length > 0 && (
            <Button onClick={handleAddAllToCart} size="lg">
              Alle in den Warenkorb
            </Button>
          )}
        </div>
        
        {products.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <ClipboardList className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-testid="empty-notepad-title">
              Ihr Merkzettel ist leer
            </h2>
            <p className="text-gray-600 mb-6">Speichern Sie Produkte mit Mengenangabe für spätere Bestellungen</p>
            <Link to="/products">
              <Button data-testid="browse-products">Produkte durchsuchen</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="notepad-list">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow p-4"
                data-testid={`notepad-item-${product.id}`}
              >
                <div className="flex items-center gap-4">
                  <Link to={`/products/${product.id}`} className="flex-shrink-0">
                    <div className="w-24 h-24 relative overflow-hidden rounded-lg">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <ShoppingBag className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </Link>
                  
                  <div className="flex-1">
                    <Link to={`/products/${product.id}`}>
                      <h3 className="font-medium text-gray-900 mb-1 hover:text-blue-600">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="text-lg font-bold text-gray-900 mb-2">{formatEUR(product.price)}</p>
                    
                    {product.is_handmade && (
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                        Handgemacht
                      </span>
                    )}
                  </div>
                  
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(product.id, product.notepad_quantity || 1, -1)}
                      disabled={product.notepad_quantity <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="text-lg font-semibold w-12 text-center">
                      {product.notepad_quantity || 1}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(product.id, product.notepad_quantity || 1, 1)}
                      disabled={product.notepad_quantity >= 999}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAddToCart(product)}
                      disabled={!(product.stock > 0)}
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      {!(product.stock > 0) ? 'Ausverkauft' : 'In den Warenkorb'}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleRemove(product.id)}
                      data-testid={`remove-${product.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notepad;
